// src/pages/ContactPage.jsx
import React, { useState, useMemo } from "react";
import { useTranslation } from "../i18n";
import { Helmet } from "react-helmet-async";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";

/* ── helpers ──────────────────────────────────────────────────────────────── */
function getToken() {
  try {
    return localStorage.getItem("accessToken") || localStorage.getItem("token") || null;
  } catch {
    return null;
  }
}

function authHeaders() {
  const t = getToken();
  const h = { "Content-Type": "application/json" };
  if (t) h.Authorization = t.startsWith("Bearer ") ? t : `Bearer ${t}`;
  return h;
}

/* ══════════════════════════════════════════════════════════════════════════════
   CSS
══════════════════════════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600;700&display=swap');

:root {
  --ct-red:    #e63311;
  --ct-red-dk: #c4280d;
  --ct-ink:    #0d0d0d;
  --ct-ink2:   #3d3d3d;
  --ct-ink3:   #767676;
  --ct-bg:     #ffffff;
  --ct-bg2:    #f8f8f7;
  --ct-bg3:    #f0eeec;
  --ct-border: rgba(0,0,0,0.08);
  --ct-shadow: 0 2px 16px rgba(0,0,0,0.07);
  --ct-shadow-h: 0 12px 40px rgba(0,0,0,0.13);
  --ct-radius: 14px;
  --ct-ease:   cubic-bezier(.4,0,.2,1);
  --ct-serif:  'Playfair Display', Georgia, serif;
  --ct-sans:   'DM Sans', -apple-system, sans-serif;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html, body, #root {
  width: 100%;
  min-height: 100%;
}

body {
  margin: 0;
  overflow-x: hidden;
}

img, svg, video, canvas {
  max-width: 100%;
  height: auto;
}

@media (prefers-color-scheme: dark) {
  :root {
    --ct-ink:    #f0ede8;
    --ct-ink2:   #c9c5be;
    --ct-ink3:   #888;
    --ct-bg:     #111110;
    --ct-bg2:    #191917;
    --ct-bg3:    #222220;
    --ct-border: rgba(255,255,255,0.07);
    --ct-shadow: 0 2px 16px rgba(0,0,0,0.4);
    --ct-shadow-h: 0 12px 40px rgba(0,0,0,0.55);
  }
}

@keyframes ct-up   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }
@keyframes ct-in   { from{opacity:0} to{opacity:1} }
@keyframes ct-pop  { 0%{transform:scale(.85)} 60%{transform:scale(1.08)} 100%{transform:scale(1)} }
@keyframes ct-shim { from{background-position:-600px 0} to{background-position:600px 0} }
@keyframes ct-spin { to{transform:rotate(360deg)} }
@keyframes ct-check{ 0%{stroke-dashoffset:24} 100%{stroke-dashoffset:0} }
@keyframes ct-bar  { from{transform:scaleX(0)} to{transform:scaleX(1)} }
@keyframes ct-pulse{ 0%,100%{opacity:1} 50%{opacity:.5} }

@media (prefers-reduced-motion:reduce) {
  *,*::before,*::after{animation:none!important;transition:none!important;}
}

.ct-page {
  width: 100%;
  font-family: var(--ct-sans);
  background: var(--ct-bg);
  color: var(--ct-ink);
  min-height: 100vh;
  overflow-x: hidden;
}

/* ═══ HERO ════════════════════════════════════════════════════════════════ */
.ct-hero {
  position:relative;
  background:var(--ct-ink);
  overflow:hidden;
  padding:clamp(48px,8vw,96px) clamp(16px,5vw,64px) clamp(32px,6vw,64px);
  animation:ct-in .4s var(--ct-ease) both;
}
.ct-hero__bg {
  position:absolute;inset:0;
  background:
    radial-gradient(ellipse at 75% 40%,rgba(230,51,17,.22) 0%,transparent 60%),
    radial-gradient(ellipse at 10% 85%,rgba(230,51,17,.08) 0%,transparent 55%);
}
.ct-hero__noise {
  position:absolute;inset:0;pointer-events:none;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.035'/%3E%3C/svg%3E");
}
.ct-hero__inner {
  position:relative;z-index:1;
  max-width:1280px;
  margin:0 auto;
  width:100%;
}
.ct-hero__eyebrow {
  display:inline-flex;align-items:center;gap:8px;
  font-size:.68rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;
  color:var(--ct-red);margin-bottom:18px;
  animation:ct-up .4s .08s var(--ct-ease) both;
}
.ct-hero__eyebrow-bar {
  display:inline-block;width:24px;height:2px;background:var(--ct-red);border-radius:2px;
  transform-origin:left;animation:ct-bar .5s .12s var(--ct-ease) both;
}
.ct-hero__title {
  font-family:var(--ct-serif);
  font-size:clamp(2.1rem,6vw,4.2rem);
  font-weight:900;
  line-height:1.05;
  letter-spacing:-.03em;
  color:rgba(2, 2, 2, 0.95);
  margin:0 0 14px;
  max-width: 560px;
  animation:ct-up .45s .14s var(--ct-ease) both;
}
.ct-hero__sub {
  font-size:clamp(.92rem,1.8vw,1.08rem);
  font-weight:300;
  color:rgba(0, 0, 0, 0.78);
  max-width:640px;
  line-height:1.72;
  margin:0;
  animation:ct-up .45s .2s var(--ct-ease) both;
}

/* ═══ CONTAINER ════════════════════════════════════════════════════════════ */
.ct-wrap {
  max-width:1280px;
  margin:0 auto;
  width:100%;
  padding:0 clamp(16px,5vw,64px);
}

/* ═══ DEPT CARDS ROW ══════════════════════════════════════════════════════ */
.ct-depts {
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(200px,1fr));
  gap:16px;
  padding:clamp(28px,5vw,56px) 0 0;
  animation:ct-up .45s .1s var(--ct-ease) both;
}
.ct-dept-card {
  min-width: 0;
  background:var(--ct-bg);
  border:1px solid var(--ct-border);
  border-radius:var(--ct-radius);
  padding:22px 20px;
  transition:transform .22s var(--ct-ease),box-shadow .22s var(--ct-ease),border-color .22s var(--ct-ease);
  cursor:default;
  animation:ct-up .4s var(--ct-ease) both;
}
.ct-dept-card:hover {
  transform:translateY(-4px);
  box-shadow:var(--ct-shadow-h);
  border-color:rgba(230,51,17,.2);
}
.ct-dept-card__icon {
  width:44px;height:44px;border-radius:12px;
  display:flex;align-items:center;justify-content:center;
  font-size:1.25rem;margin-bottom:14px;
  background:rgba(230,51,17,.08);
  transition:background .2s;
}
.ct-dept-card:hover .ct-dept-card__icon { background:rgba(230,51,17,.15); }
.ct-dept-card__name {
  font-size:.9rem;
  font-weight:700;
  color:var(--ct-ink);
  margin-bottom:4px;
  overflow-wrap:anywhere;
}
.ct-dept-card__email {
  display:inline-block;
  max-width:100%;
  font-size:.75rem;
  color:var(--ct-red);
  text-decoration:none;
  font-weight:500;
  transition:opacity .15s;
  overflow-wrap:anywhere;
  word-break:break-word;
}
.ct-dept-card__email:hover { opacity:.75; }
.ct-dept-card__desc {
  font-size:.75rem;
  color:var(--ct-ink3);
  margin-top:6px;
  line-height:1.55;
  overflow-wrap:anywhere;
}

/* ═══ MAIN GRID ════════════════════════════════════════════════════════════ */
.ct-main {
  display:grid;
  grid-template-columns:minmax(0,1fr) minmax(280px,400px);
  gap:clamp(20px,4vw,48px);
  padding:clamp(36px,6vw,64px) 0 clamp(56px,8vw,80px);
  align-items:start;
  width:100%;
  min-width:0;
}
@media (max-width:1024px) {
  .ct-main { grid-template-columns:1fr; }
}

/* ═══ FORM CARD ════════════════════════════════════════════════════════════ */
.ct-form-card {
  min-width:0;
  background:var(--ct-bg);
  border:1px solid var(--ct-border);
  border-radius:var(--ct-radius);
  padding:clamp(20px,4vw,40px);
  box-shadow:var(--ct-shadow);
  animation:ct-up .45s .12s var(--ct-ease) both;
}
.ct-form-card__head  { margin-bottom:28px; }
.ct-form-card__title {
  font-family:var(--ct-serif);
  font-size:clamp(1.35rem,2vw,1.55rem);
  font-weight:700;
  margin:0 0 6px;
  letter-spacing:-.02em;
  color:rgba(255,255,255,.84);
}
.ct-form-card__sub {
  font-size:.84rem;
  color:var(--ct-ink3);
  margin:0;
  line-height:1.55;
}

.ct-form {
  display:flex;
  flex-direction:column;
  gap:18px;
  min-width:0;
}

/* Row of 2 fields side by side */
.ct-form-row {
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:14px;
}
@media (max-width:520px) {
  .ct-form-row { grid-template-columns:1fr; }
}

.ct-group {
  display:flex;
  flex-direction:column;
  gap:6px;
  min-width:0;
}
.ct-label {
  font-size:.72rem;
  font-weight:700;
  letter-spacing:.07em;
  text-transform:uppercase;
  color:var(--ct-ink2);
  opacity:.7;
}
.ct-label span { color:var(--ct-red);margin-left:2px; }

.ct-input, .ct-select, .ct-textarea {
  width:100%;
  min-width:0;
  padding:12px 14px;
  background:var(--ct-bg2);
  border:1.5px solid var(--ct-border);
  border-radius:10px;
  font-size:.9rem;
  font-family:var(--ct-sans);
  color:var(--ct-ink);
  outline:none;
  transition:all .18s var(--ct-ease);
}
.ct-textarea {
  resize:vertical;
  min-height:130px;
  line-height:1.6;
}
.ct-input::placeholder,.ct-textarea::placeholder { color:var(--ct-ink3);opacity:.7; }
.ct-input:focus,.ct-select:focus,.ct-textarea:focus {
  border-color:var(--ct-red);
  box-shadow:0 0 0 3px rgba(230,51,17,.1);
  background:var(--ct-bg);
  transform:translateY(-1px);
}
.ct-input.error,.ct-select.error,.ct-textarea.error {
  border-color:#ef4444;
  background:rgba(239,68,68,.03);
}
.ct-input.error:focus,.ct-textarea.error:focus {
  box-shadow:0 0 0 3px rgba(239,68,68,.1);
}

.ct-select {
  appearance:none;
  cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat;
  background-position:right 12px center;
  padding-right:36px;
}

.ct-field-error {
  font-size:.72rem;
  color:#ef4444;
  display:flex;
  align-items:flex-start;
  gap:5px;
  overflow-wrap:anywhere;
}

/* Character counter */
.ct-char-count {
  font-size:.68rem;
  color:var(--ct-ink3);
  text-align:right;
}
.ct-char-count.warn { color:#f59e0b; }
.ct-char-count.over { color:#ef4444; }

/* Submit button */
.ct-submit {
  display:flex;
  align-items:center;
  justify-content:center;
  gap:10px;
  width:100%;
  padding:14px 28px;
  margin-top:4px;
  background:var(--ct-ink);
  color:var(--ct-bg);
  border:none;
  border-radius:100px;
  font-size:.88rem;
  font-weight:700;
  letter-spacing:.05em;
  text-transform:uppercase;
  cursor:pointer;
  font-family:var(--ct-sans);
  transition:all .2s var(--ct-ease);
  box-shadow:0 4px 16px rgba(0,0,0,.12);
}
.ct-submit:hover:not(:disabled) {
  background:var(--ct-red);
  transform:translateY(-2px);
  box-shadow:0 8px 28px rgba(230,51,17,.3);
}
.ct-submit:active:not(:disabled) { transform:translateY(0); }
.ct-submit:disabled { opacity:.55;cursor:not-allowed; }
.ct-submit__spinner {
  width:16px;
  height:16px;
  border:2px solid rgba(255,255,255,.35);
  border-top-color:#fff;
  border-radius:50%;
  animation:ct-spin .75s linear infinite;
}

/* Success panel */
.ct-success {
  display:flex;
  flex-direction:column;
  align-items:center;
  text-align:center;
  gap:16px;
  padding:40px 20px;
  animation:ct-up .45s var(--ct-ease) both;
}
.ct-success__icon {
  width:72px;height:72px;border-radius:50%;
  background:rgba(34,197,94,.1);
  border:2px solid rgba(34,197,94,.25);
  display:flex;align-items:center;justify-content:center;
  animation:ct-pop .5s var(--ct-ease) both;
}
.ct-success__icon svg { animation:ct-check .5s .2s var(--ct-ease) forwards; }
.ct-success__title {
  font-family:var(--ct-serif);
  font-size:1.4rem;
  font-weight:700;
  color:var(--ct-ink);
  margin:0;
}
.ct-success__sub {
  font-size:.88rem;
  color:var(--ct-ink3);
  max-width:340px;
  line-height:1.65;
}
.ct-success__back {
  padding:11px 24px;
  border-radius:100px;
  border:1.5px solid var(--ct-border);
  background:var(--ct-bg2);
  color:var(--ct-ink2);
  font-size:.82rem;
  font-weight:700;
  font-family:var(--ct-sans);
  cursor:pointer;
  transition:all .18s var(--ct-ease);
}
.ct-success__back:hover {
  border-color:rgba(230,51,17,.3);
  color:var(--ct-red);
  background:rgba(230,51,17,.05);
}

/* ═══ RIGHT COLUMN ════════════════════════════════════════════════════════ */
.ct-sidebar {
  display:flex;
  flex-direction:column;
  gap:20px;
  min-width:0;
  animation:ct-up .45s .18s var(--ct-ease) both;
}

/* Info card */
.ct-info-card {
  background:var(--ct-bg);
  border:1px solid var(--ct-border);
  border-radius:var(--ct-radius);
  padding:24px;
  box-shadow:var(--ct-shadow);
}
.ct-info-card__title {
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:var(--ct-ink3);
  margin-bottom:16px;
}
.ct-info-list {
  display:flex;
  flex-direction:column;
  gap:14px;
  list-style:none;
  padding:0;
  margin:0;
}
.ct-info-item {
  display:flex;
  align-items:flex-start;
  gap:12px;
  min-width:0;
}
.ct-info-item__icon {
  width:36px;height:36px;min-width:36px;border-radius:10px;
  display:flex;align-items:center;justify-content:center;
  background:rgba(230,51,17,.08);
  font-size:1rem;
  flex-shrink:0;
}
.ct-info-item__label {
  font-size:.7rem;
  font-weight:700;
  letter-spacing:.05em;
  text-transform:uppercase;
  color:var(--ct-ink3);
  margin-bottom:2px;
}
.ct-info-item__value {
  font-size:.84rem;
  color:var(--ct-ink);
  font-weight:500;
  line-height:1.45;
  overflow-wrap:anywhere;
  word-break:break-word;
}
.ct-info-item__value a {
  color:var(--ct-red);
  text-decoration:none;
  transition:opacity .15s;
}
.ct-info-item__value a:hover { opacity:.75; }

/* Response time badge */
.ct-response-badge {
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:7px 12px;
  border-radius:100px;
  background:rgba(34,197,94,.08);
  border:1px solid rgba(34,197,94,.2);
  font-size:.72rem;
  font-weight:700;
  color:#16a34a;
  flex-wrap:wrap;
}
.ct-response-badge::before {
  content:'';
  width:7px;height:7px;border-radius:50%;
  background:#22c55e;
  box-shadow:0 0 0 2px rgba(34,197,94,.25);
  animation:ct-pulse 2s ease-in-out infinite;
}

/* Social links */
.ct-socials-card {
  background:var(--ct-bg);
  border:1px solid var(--ct-border);
  border-radius:var(--ct-radius);
  padding:20px 24px;
  box-shadow:var(--ct-shadow);
}
.ct-socials-card__title {
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:var(--ct-ink3);
  margin-bottom:14px;
}
.ct-socials {
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(140px,1fr));
  gap:10px;
}
.ct-social-btn {
  display:flex;
  align-items:center;
  gap:8px;
  width:100%;
  padding:9px 14px;
  border-radius:9px;
  border:1.5px solid var(--ct-border);
  background:var(--ct-bg2);
  font-size:.78rem;
  font-weight:600;
  color:var(--ct-ink2);
  text-decoration:none;
  transition:all .18s var(--ct-ease);
  font-family:var(--ct-sans);
  min-width:0;
  overflow:hidden;
}
.ct-social-btn:hover {
  transform:translateY(-2px);
  box-shadow:0 4px 14px rgba(0,0,0,.1);
}
.ct-social-btn span:last-child {
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

/* Map card */
.ct-map-card {
  background:var(--ct-bg);
  border:1px solid var(--ct-border);
  border-radius:var(--ct-radius);
  overflow:hidden;
  box-shadow:var(--ct-shadow);
}
.ct-map-card__head { padding:16px 20px;border-bottom:1px solid var(--ct-border); }
.ct-map-card__title {
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.1em;
  text-transform:uppercase;
  color:var(--ct-ink3);
  margin:0;
}
.ct-map-placeholder {
  height:180px;
  background:var(--ct-bg2);
  display:flex;
  align-items:center;
  justify-content:center;
  flex-direction:column;
  gap:10px;
  color:var(--ct-ink3);
  font-size:.84rem;
}
.ct-map-placeholder__icon { font-size:2rem;opacity:.4; }

/* ═══ FAQ SECTION ══════════════════════════════════════════════════════════ */
.ct-faq-section {
  padding:0 0 clamp(56px,8vw,80px);
  animation:ct-up .45s .15s var(--ct-ease) both;
}
.ct-faq-head { margin-bottom:28px; }
.ct-faq-head__eyebrow {
  font-size:.68rem;
  font-weight:700;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:var(--ct-red);
  margin-bottom:8px;
  display:flex;
  align-items:center;
  gap:7px;
}
.ct-faq-head__eyebrow-line { width:20px;height:2px;background:var(--ct-red);border-radius:2px; }
.ct-faq-head__title {
  font-family:var(--ct-serif);
  font-size:clamp(1.35rem,3vw,2rem);
  font-weight:800;
  letter-spacing:-.02em;
  margin:0;
  color:rgba(233, 112, 112, 0.93);
}

.ct-faq-list {
  display:flex;
  flex-direction:column;
  gap:10px;
  max-width:760px;
  width:100%;
}
.ct-faq-item {
  width:100%;
  background:var(--ct-bg);
  border:1px solid var(--ct-border);
  border-radius:var(--ct-radius);
  overflow:hidden;
  transition:border-color .18s var(--ct-ease),box-shadow .18s var(--ct-ease);
}
.ct-faq-item.open {
  border-color:rgba(230,51,17,.2);
  box-shadow:0 4px 20px rgba(230,51,17,.06);
}
.ct-faq-btn {
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  width:100%;
  padding:18px 20px;
  background:none;
  border:none;
  cursor:pointer;
  text-align:left;
  font-family:var(--ct-sans);
}
.ct-faq-btn:hover .ct-faq-q { color:var(--ct-red); }
.ct-faq-q {
  font-size:.9rem;
  font-weight:600;
  color:var(--ct-ink);
  transition:color .15s;
  flex:1;
  min-width:0;
  overflow-wrap:anywhere;
}
.ct-faq-chevron {
  font-size:.7rem;
  color:var(--ct-ink3);
  transition:transform .22s var(--ct-ease);
  flex-shrink:0;
}
.ct-faq-item.open .ct-faq-chevron {
  transform:rotate(180deg);
  color:var(--ct-red);
}
.ct-faq-body {
  max-height:0;
  overflow:hidden;
  transition:max-height .3s var(--ct-ease),padding .3s var(--ct-ease);
}
.ct-faq-item.open .ct-faq-body { max-height:420px; }
.ct-faq-a {
  padding:0 20px 18px;
  font-size:.84rem;
  color:var(--ct-ink3);
  line-height:1.7;
  border-top:1px solid var(--ct-border);
  padding-top:14px;
  overflow-wrap:anywhere;
}

/* ═══ RTL ══════════════════════════════════════════════════════════════════ */
[dir="rtl"] .ct-hero__eyebrow-bar { transform-origin:right; }
[dir="rtl"] .ct-select {
  background-position:left 12px center;
  padding-right:14px;
  padding-left:36px;
}
[dir="rtl"] .ct-faq-btn { text-align:right; }

/* ═══ RESPONSIVE ═══════════════════════════════════════════════════════════ */
@media (max-width: 1200px) {
  .ct-depts {
    grid-template-columns:repeat(auto-fit,minmax(180px,1fr));
  }
}

@media (max-width: 1024px) {
  .ct-hero {
    padding-top: 54px;
  }
  .ct-main {
    grid-template-columns:1fr;
  }
  .ct-sidebar {
    display:grid;
    grid-template-columns:repeat(2,minmax(0,1fr));
    gap:16px;
  }
  .ct-info-card,
  .ct-socials-card,
  .ct-map-card {
    height: 100%;
  }
}

@media (max-width: 768px) {
  .ct-wrap {
    padding: 0 16px;
  }
  .ct-depts {
    grid-template-columns:repeat(auto-fit,minmax(160px,1fr));
    gap:12px;
  }
  .ct-form-card,
  .ct-info-card,
  .ct-socials-card {
    padding:20px;
  }
  .ct-sidebar {
    grid-template-columns:1fr;
  }
  .ct-socials {
    grid-template-columns:repeat(auto-fit,minmax(120px,1fr));
  }
  .ct-hero__title {
    max-width: 100%;
  }
  .ct-hero__sub {
    max-width: 100%;
  }
}

@media (max-width: 520px) {
  .ct-depts {
    grid-template-columns:1fr;
  }
  .ct-form-row {
    grid-template-columns:1fr;
  }
  .ct-btn-full,
  .ct-submit,
  .ct-social-btn {
    width:100%;
  }
  .ct-btn-full {
    justify-content:center;
  }
  .ct-btn-full span:last-child,
  .ct-social-btn span:last-child {
    white-space:normal;
  }
  .ct-faq-btn {
    padding:16px;
  }
  .ct-faq-a {
    padding-left:16px;
    padding-right:16px;
  }
  .ct-info-card__title,
  .ct-socials-card__title,
  .ct-faq-head__eyebrow {
    letter-spacing:.08em;
  }
  .ct-hero {
    padding:44px 16px 28px;
  }
  .ct-hero__title {
    font-size: clamp(2rem, 10vw, 2.7rem);
  }
  .ct-hero__sub {
    font-size: .95rem;
    line-height: 1.65;
  }
}

@media (max-width: 420px) {
  .ct-form-card__title {
    font-size: 1.3rem;
  }
  .ct-char-count {
    text-align:left;
  }
}
`;

/* ══════════════════════════════════════════════════════════════════════════════
   DATA
══════════════════════════════════════════════════════════════════════════════ */
function getDepts(t) {
  return [
    {
      value: "editorial",
      icon: "📰",
      name: t("dept_editorial") || "Editorial",
      email: "newsteam@24newsread.com",
      desc: t("dept_editorial_desc") || "Story tips, corrections, press releases",
    },
    {
      value: "advertising",
      icon: "📢",
      name: t("dept_advertising") || "Advertising",
      email: "newsteam@24newsread.com",
      desc: t("dept_advertising_desc") || "Ad placements, partnerships, sponsorships",
    },
    {
      value: "partnerships",
      icon: "🤝",
      name: t("dept_partners") || "Partnerships",
      email: "newsteam@24newsread.com",
      desc: t("dept_partners_desc") || "Business collaborations and media deals",
    },
    {
      value: "support",
      icon: "💬",
      name: t("dept_support") || "Support",
      email: "newsteam@24newsread.com",
      desc: t("dept_support_desc") || "Account help, technical issues",
    },
  ];
}

function getFAQs(t) {
  return [
    {
      q: t("faq_q1") || "How long does it take to get a response?",
      a: t("faq_a1") || "We aim to respond to all inquiries within 24–48 business hours. Urgent editorial matters are typically handled within a few hours.",
    },
    {
      q: t("faq_q2") || "How can I submit a news tip or story idea?",
      a: t("faq_a2") || "Use the contact form on this page, select 'Editorial' as the department, and describe your tip. You can also email newsteam@24newsread.com directly.",
    },
    {
      q: t("faq_q3") || "I found an error in an article — how do I report it?",
      a: t("faq_a3") || "We take accuracy seriously. Please contact our Editorial team with the article URL and a description of the error. We review and issue corrections promptly.",
    },
    {
      q: t("faq_q4") || "How can I advertise on 24News?",
      a: t("faq_a4") || "For advertising opportunities including banners, sponsored content, and newsletter placements, contact our Advertising team at ads@24newsread.com.",
    },
    {
      q: t("faq_q5") || "Is my information kept confidential?",
      a: t("faq_a5") || "Yes. Your contact details are used only to respond to your inquiry and are never shared with third parties. See our Privacy Policy for full details.",
    },
  ];
}

const SOCIALS = [
  { icon: "bi-twitter-x", href: "https://x.com/0news_247969", labelKey: "twitter_label" },
  { icon: "bi-facebook", href: "https://www.facebook.com/profile.php?id=61579447607022", labelKey: "facebook_label" },
  { icon: "bi-instagram", href: "https://www.instagram.com/24.0_news/", labelKey: "instagram_label" },
  { icon: "bi-youtube", href: "https://youtube.com/@news24-now?si=SRo_3hctE-kQBzZr", labelKey: "youtube_label" },
];

/* ══════════════════════════════════════════════════════════════════════════════
   SUB-COMPONENTS
══════════════════════════════════════════════════════════════════════════════ */
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`ct-faq-item${open ? " open" : ""}`}>
      <button className="ct-faq-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="ct-faq-q">{q}</span>
        <span className="ct-faq-chevron" aria-hidden>
          ▼
        </span>
      </button>
      <div className="ct-faq-body">
        <p className="ct-faq-a">{a}</p>
      </div>
    </div>
  );
}

const MAX_MSG = 1200;

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════════════════════ */
export default function ContactPage() {
  const { t, lang } = useTranslation();
  const isRTL = lang === "ar";
  const depts = useMemo(() => getDepts(t), [t]);
  const faqs = useMemo(() => getFAQs(t), [t]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    department: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [srvErr, setSrvErr] = useState("");

  const msgChars = form.message.length;

  const pageTitle = t("contact_title") || "Get in Touch";
  const pageDescription =
    t("contact_subtitle") || "We'd love to hear from you — tips, partnerships, support, or just a hello.";

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("contact_err_name") || "Name is required.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = t("contact_err_email") || "Valid email is required.";
    }
    if (!form.department) e.department = t("contact_err_dept") || "Please select a department.";
    if (!form.subject.trim()) e.subject = t("contact_err_subject") || "Subject is required.";
    if (!form.message.trim()) e.message = t("contact_err_message") || "Message is required.";
    if (msgChars > MAX_MSG) e.message = t("contact_err_long") || "Message is too long.";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    if (status === "error") {
      setStatus("idle");
      setSrvErr("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setStatus("loading");
    setSrvErr("");

    try {
      await axios.post(
        `${API_BASE}/contact/`,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          department: form.department,
          subject: form.subject.trim(),
          message: form.message.trim(),
        },
        { headers: authHeaders() }
      );

      console.log("Contact form submitted:", form);
      setStatus("success");
    } catch (err) {
      console.log("Backend response:", err.response?.data);

      const data = err.response?.data;
      const firstError =
        data && typeof data === "object"
          ? Object.values(data).flat?.()?.[0] ?? Object.values(data).flat()[0]
          : null;

      setSrvErr(firstError || t("contact_send_error") || "Failed to send. Please try again.");
      setStatus("error");
      console.error("Contact form submission error:", data);
    }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", department: "", subject: "", message: "" });
    setErrors({});
    setStatus("idle");
    setSrvErr("");
  };

  return (
    <div className="ct-page" dir={isRTL ? "rtl" : "ltr"}>
      <Helmet
        htmlAttributes={{
          lang: lang || "en",
          dir: isRTL ? "rtl" : "ltr",
        }}
      >
        <title>{pageTitle} | 24News</title>
        <meta name="description" content={pageDescription} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${pageTitle} | 24News`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="24News" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${pageTitle} | 24News`} />
        <meta name="twitter:description" content={pageDescription} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: pageTitle,
            description: pageDescription,
            url: typeof window !== "undefined" ? window.location.href : undefined,
            publisher: {
              "@type": "Organization",
              name: "24News",
            },
          })}
        </script>
         <link rel="canonical" href={`${window.location.origin}/${lang}/contact`} />
        
      </Helmet>

      <style>{CSS}</style>

      <section className="ct-hero">
        <div className="ct-hero__bg" aria-hidden />
        <div className="ct-hero__noise" aria-hidden />
        <div className="ct-hero__inner">
          <div className="ct-hero__eyebrow">
            <span className="ct-hero__eyebrow-bar" aria-hidden />
            24News
          </div>
          <h1 className="ct-hero__title">{pageTitle}</h1>
          <p className="ct-hero__sub">{pageDescription}</p>
        </div>
      </section>

      <div className="ct-wrap">
        <div className="ct-depts">
          {depts.map((d, i) => (
            <div key={d.value} className="ct-dept-card" style={{ animationDelay: `${0.05 + i * 0.06}s` }}>
              <div className="ct-dept-card__icon" aria-hidden>
                {d.icon}
              </div>
              <div className="ct-dept-card__name">{d.name}</div>
              <a href={`mailto:${d.email}`} className="ct-dept-card__email">
                {d.email}
              </a>
              <p className="ct-dept-card__desc">{d.desc}</p>
            </div>
          ))}
        </div>

        <div className="ct-main">
          <div className="ct-form-card">
            {status === "success" ? (
              <div className="ct-success">
                <div className="ct-success__icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round">
                    <polyline
                      points="20 6 9 17 4 12"
                      strokeDasharray="24"
                      strokeDashoffset="24"
                      style={{ animation: "ct-check .5s .2s cubic-bezier(.4,0,.2,1) forwards" }}
                    />
                  </svg>
                </div>
                <h2 className="ct-success__title">{t("contact_success_title") || "Message Sent!"}</h2>
                <p className="ct-success__sub">
                  {t("contact_success_sub") || "Thank you for reaching out. We'll get back to you within 24–48 hours."}
                </p>
                <button className="ct-success__back" onClick={resetForm}>
                  {t("contact_send_another") || "Send another message"}
                </button>
              </div>
            ) : (
              <>
                <div className="ct-form-card__head">
                  <h2 className="ct-form-card__title">{t("contact_form_title") || "Send us a message"}</h2>
                  <p className="ct-form-card__sub">{t("contact_form_sub") || "All fields marked with * are required."}</p>
                </div>

                <form className="ct-form" onSubmit={handleSubmit} noValidate>
                  <div className="ct-form-row">
                    <div className="ct-group">
                      <label className="ct-label" htmlFor="ct-name">
                        {t("contact_label_name") || "Your name"} <span>*</span>
                      </label>
                      <input
                        id="ct-name"
                        name="name"
                        type="text"
                        className={`ct-input${errors.name ? " error" : ""}`}
                        placeholder={t("contact_ph_name") || "John Doe"}
                        value={form.name}
                        onChange={handleChange}
                        disabled={status === "loading"}
                        autoComplete="name"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "err-name" : undefined}
                      />
                      {errors.name && (
                        <span className="ct-field-error" id="err-name" role="alert">
                          ⚠ {errors.name}
                        </span>
                      )}
                    </div>

                    <div className="ct-group">
                      <label className="ct-label" htmlFor="ct-email">
                        {t("contact_label_email") || "Email address"} <span>*</span>
                      </label>
                      <input
                        id="ct-email"
                        name="email"
                        type="email"
                        className={`ct-input${errors.email ? " error" : ""}`}
                        placeholder={t("contact_ph_email") || "you@example.com"}
                        value={form.email}
                        onChange={handleChange}
                        disabled={status === "loading"}
                        autoComplete="email"
                        inputMode="email"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "err-email" : undefined}
                      />
                      {errors.email && (
                        <span className="ct-field-error" id="err-email" role="alert">
                          ⚠ {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="ct-group">
                    <label className="ct-label" htmlFor="ct-dept">
                      {t("contact_label_dept") || "Department"} <span>*</span>
                    </label>
                    <select
                      id="ct-dept"
                      name="department"
                      className={`ct-select${errors.department ? " error" : ""}`}
                      value={form.department}
                      onChange={handleChange}
                      disabled={status === "loading"}
                      aria-invalid={!!errors.department}
                    >
                      <option value="">{t("contact_select_dept") || "Select a department…"}</option>
                      {depts.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                    {errors.department && (
                      <span className="ct-field-error" role="alert">
                        ⚠ {errors.department}
                      </span>
                    )}
                  </div>

                  <div className="ct-group">
                    <label className="ct-label" htmlFor="ct-subject">
                      {t("contact_label_subject") || "Subject"} <span>*</span>
                    </label>
                    <input
                      id="ct-subject"
                      name="subject"
                      type="text"
                      className={`ct-input${errors.subject ? " error" : ""}`}
                      placeholder={t("contact_ph_subject") || "What's this about?"}
                      value={form.subject}
                      onChange={handleChange}
                      disabled={status === "loading"}
                      aria-invalid={!!errors.subject}
                    />
                    {errors.subject && (
                      <span className="ct-field-error" role="alert">
                        ⚠ {errors.subject}
                      </span>
                    )}
                  </div>

                  <div className="ct-group">
                    <label className="ct-label" htmlFor="ct-message">
                      {t("contact_label_message") || "Message"} <span>*</span>
                    </label>
                    <textarea
                      id="ct-message"
                      name="message"
                      className={`ct-textarea${errors.message ? " error" : ""}`}
                      placeholder={t("contact_ph_message") || "Tell us more…"}
                      value={form.message}
                      onChange={handleChange}
                      disabled={status === "loading"}
                      aria-invalid={!!errors.message}
                      maxLength={MAX_MSG + 50}
                    />
                    <div
                      className="ct-char-count"
                      style={{
                        color:
                          msgChars > MAX_MSG
                            ? "#ef4444"
                            : msgChars > MAX_MSG * 0.85
                            ? "#f59e0b"
                            : undefined,
                      }}
                    >
                      {msgChars} / {MAX_MSG}
                    </div>
                    {errors.message && (
                      <span className="ct-field-error" role="alert">
                        ⚠ {errors.message}
                      </span>
                    )}
                  </div>

                  {status === "error" && srvErr && (
                    <div
                      style={{
                        padding: "12px 14px",
                        background: "rgba(239,68,68,.06)",
                        border: "1px solid rgba(239,68,68,.2)",
                        borderRadius: "9px",
                        fontSize: ".82rem",
                        color: "#b91c1c",
                        fontWeight: 500,
                        overflowWrap: "anywhere",
                      }}
                    >
                      ⚠ {srvErr}
                    </div>
                  )}

                  <button type="submit" className="ct-submit" disabled={status === "loading"} aria-busy={status === "loading"}>
                    {status === "loading" ? (
                      <>
                        <div className="ct-submit__spinner" aria-hidden />
                        {t("contact_sending") || "Sending…"}
                      </>
                    ) : (
                      <>{t("contact_send") || "Send message"} →</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          <div className="ct-sidebar">
            <div className="ct-info-card">
              <div className="ct-info-card__title">{t("contact_info_title") || "Contact Information"}</div>
              <div className="ct-response-badge" style={{ marginBottom: 16 }}>
                {t("contact_response_time") || "Avg. response time: under 24h"}
              </div>
              <ul className="ct-info-list">
                {[
                  { icon: "📧", label: t("contact_info_general") || "General", value: <a href="mailto:newsteam@24newsread.com">newsteam@24newsread.com</a> },
                  { icon: "⏰", label: t("contact_info_hours") || "Hours", value: t("contact_hours_val") || "Mon – Fri, 9 AM – 6 PM (GMT+1)" },
                ].map((item, i) => (
                  <li key={i} className="ct-info-item">
                    <div className="ct-info-item__icon" aria-hidden>
                      {item.icon}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="ct-info-item__label">{item.label}</div>
                      <div className="ct-info-item__value">{item.value}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="ct-socials-card">
              <div className="ct-socials-card__title">{t("contact_socials_title") || "Follow Us"}</div>
              <div className="ct-socials">
                {SOCIALS.map((s, index) => (
                  <a
                    key={index}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ct-social-btn"
                    aria-label={t(s.labelKey) || s.labelKey}
                  >
                    <span aria-hidden>
                      <i className={`bi ${s.icon}`} />
                    </span>
                    <span>{t(s.labelKey) || s.labelKey}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="ct-faq-section">
          <div className="ct-faq-head">
            <div className="ct-faq-head__eyebrow">
              <span className="ct-faq-head__eyebrow-line" aria-hidden />
              {t("faq_eyebrow") || "FAQ"}
            </div>
            <h2 className="ct-faq-head__title">{t("faq_title") || "Frequently Asked Questions"}</h2>
          </div>
          <div className="ct-faq-list">
            {faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
